import React, { useState, useEffect, useCallback } from "react";
import { useMutation, gql } from "@apollo/client";
import {
  Grid,
  Typography,
  Box,
  Button,
  Switch,
  Card,
  CardContent,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Alert,
  Snackbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Delete,
  Wallpaper,
  Image as ImageIcon,
  DesktopMac,
  Code as CodeIcon,
  AlignHorizontalLeft,
  AlignHorizontalRight,
  Check,
  ContentCopy,
  Chat,
  ChatBubble,
} from "@mui/icons-material";

import Code from "../../../components/Code";

// Styled Components
const ConfigPanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  height: "100%",
  backgroundColor: theme.palette.background.paper,
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const SelectableCard = styled(Card)(({ theme, selected }) => ({
  cursor: "pointer",
  transition: "all 0.2s ease",
  border: selected
    ? `2px solid ${theme.palette.primary.main}`
    : `2px solid ${theme.palette.divider}`,
  borderRadius: 12,
  overflow: "hidden",
  backgroundColor: selected
    ? theme.palette.primary.main + "08"
    : theme.palette.background.paper,
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[4],
    borderColor: theme.palette.primary.main,
  },
}));

const PositionButtonGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  width: "100%",
}));

const PositionButton = styled(Button)(({ theme, selected }) => ({
  flex: 1,
  borderRadius: 8,
  padding: theme.spacing(1.5, 3),
  backgroundColor: selected
    ? theme.palette.primary.main
    : theme.palette.grey[100],
  color: selected ? "white" : theme.palette.text.primary,
  border: "none",
  textTransform: "none",
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  "&:hover": {
    backgroundColor: selected
      ? theme.palette.primary.dark
      : theme.palette.grey[200],
  },
}));

const UploadArea = styled(Box)(({ theme, hasImage }) => ({
  border: `2px dashed ${
    hasImage ? theme.palette.primary.main : theme.palette.grey[300]
  }`,
  borderRadius: 12,
  padding: theme.spacing(2),
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.2s ease",
  backgroundColor: hasImage ? theme.palette.primary.main + "08" : "transparent",
  minHeight: 120,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const PreviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: "#f5f7fa",
  minHeight: 500,
  display: "flex",
  flexDirection: "column",
}));

const WebsiteMockup = styled(Box)(({ theme, backgroundImage }) => ({
  flex: 1,
  borderRadius: 8,
  backgroundColor: backgroundImage ? "transparent" : "#ffffff",
  backgroundImage: backgroundImage ? `url(${backgroundImage})` : "none",
  backgroundSize: "cover",
  backgroundPosition: "center",
  position: "relative",
  overflow: "hidden",
  boxShadow: theme.shadows[2],
  minHeight: 400,
}));

const WidgetButtonPreview = styled(Button)(
  ({ theme, position, square, hasText }) => ({
    position: "absolute",
    [position]: 20,
    bottom: 20,
    borderRadius: square ? 4 : 50,
    padding: hasText ? theme.spacing(1.5, 3) : theme.spacing(1.5),
    minWidth: hasText ? "auto" : 56,
    height: hasText ? "auto" : 56,
    boxShadow: theme.shadows[4],
    textTransform: "none",
    fontWeight: 600,
  })
);

const LogoPreview = styled("img")({
  position: "absolute",
  top: 16,
  left: 16,
  maxWidth: 80,
  maxHeight: 80,
  objectFit: "contain",
  borderRadius: 8,
  backgroundColor: "rgba(255,255,255,0.9)",
  padding: 4,
});

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: 8,
  padding: 4,
  "& .MuiToggleButton-root": {
    border: "none",
    borderRadius: 6,
    padding: theme.spacing(1, 3),
    textTransform: "none",
    fontWeight: 500,
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.main,
      color: "white",
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}));

const ImagePreviewBox = styled(Box)({
  position: "relative",
  width: "100%",
  "& img": {
    width: "100%",
    maxHeight: 80,
    objectFit: "contain",
    borderRadius: 8,
  },
});

const DeleteIconButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: -8,
  right: -8,
  backgroundColor: theme.palette.error.main,
  color: "white",
  padding: 4,
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
  },
  "& .MuiSvgIcon-root": {
    fontSize: 16,
  },
}));

const AppearanceToggle = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: 8,
  marginBottom: theme.spacing(1),
  "&:last-child": {
    marginBottom: 0,
  },
}));

const DeveloperPanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  backgroundColor: theme.palette.background.paper,
}));

const CodeContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#1e1e1e",
  borderRadius: 8,
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  position: "relative",
}));

const UPDATE_WIDGET = gql`
  mutation UpdateWidget($id: ID!, $input: WidgetInput!) {
    updateWidget(id: $id, input: $input) {
      id
      website_integration {
        mode
        button_style
        position
        square_buttom
        media
        minimize_small_button
        picture
        preview
        speeach_bubble_test
        call_to_action_button_text
        slogan
      }
    }
  }
`;

const DisplayOptions = ({
  handleChildData,
  widgetData,
  isEditing,
  widgetId,
  onSave,
}) => {
  // Initialize state with widget data or defaults
  const [buttonStyle, setButtonStyle] = useState(
    widgetData?.website_integration?.button_style === 1 ? "Button" : "Standard"
  );
  const [mode, setMode] = useState(
    widgetData?.website_integration?.mode === 1 ? "Standard" : "Standard"
  );
  const [position, setPosition] = useState(
    widgetData?.website_integration?.position || "left"
  );
  const [squareButton, setSquareButton] = useState(
    widgetData?.website_integration?.square_buttom ?? true
  );
  const [minimizeSmallButton, setMinimizeSmallButton] = useState(
    widgetData?.website_integration?.minimize_small_button ?? true
  );
  const [showPreview, setShowPreview] = useState(
    widgetData?.website_integration?.preview ?? true
  );
  const [background, setBackground] = useState(
    widgetData?.website_integration?.media || null
  );
  const [logo, setLogo] = useState(
    widgetData?.website_integration?.picture || null
  );
  const [copySuccess, setCopySuccess] = useState(false);

  // Update state when widget data changes (for edit mode)
  useEffect(() => {
    if (widgetData?.website_integration) {
      const integration = widgetData.website_integration;
      setButtonStyle(integration.button_style === 1 ? "Button" : "Standard");
      setMode(integration.mode === 1 ? "Standard" : "Standard");
      setPosition(integration.position || "left");
      setSquareButton(integration.square_buttom ?? true);
      setMinimizeSmallButton(integration.minimize_small_button ?? true);
      setShowPreview(integration.preview ?? true);
      setBackground(integration.media || null);
      setLogo(integration.picture || null);
    }
  }, [widgetData]);

  const [updateWidget] = useMutation(UPDATE_WIDGET, {
    onError: (error) => {
      console.error("Error updating widget display options:", error);
    },
  });

  const handleSaveDisplayOptions = useCallback(async () => {
    if (!widgetId) return;

    try {
      const widgetInput = {
        business_details: widgetData?.business_details
          ? {
              business_type: widgetData.business_details.business_type,
              name: widgetData.business_details.name,
              display_name: widgetData.business_details.display_name,
              website_url: widgetData.business_details.website_url,
            }
          : undefined,
        website_integration: {
          mode: mode === "Standard" ? 1 : 0,
          button_style: buttonStyle === "Button" ? 1 : 0,
          position: position,
          square_buttom: squareButton,
          minimize_small_button: minimizeSmallButton,
          preview: showPreview,
          media: background,
          picture: logo,
          // Keep existing values for other fields
          speeach_bubble_test:
            widgetData?.website_integration?.speeach_bubble_test,
          call_to_action_button_text:
            widgetData?.website_integration?.call_to_action_button_text,
          slogan: widgetData?.website_integration?.slogan,
        },
        welcome_screen: widgetData?.welcome_screen
          ? {
              font_shadow: widgetData.welcome_screen.font_shadow,
              desc: widgetData.welcome_screen.desc,
              customizable_button:
                widgetData.welcome_screen.customizable_button,
              button_color: widgetData.welcome_screen.button_color,
              font_color: widgetData.welcome_screen.font_color,
              icon_color: widgetData.welcome_screen.icon_color,
              welcome_message: widgetData.welcome_screen.welcome_message,
            }
          : undefined,
      };

      const { data } = await updateWidget({
        variables: {
          id: widgetId,
          input: widgetInput,
        },
      });

      console.log("Display options updated successfully:", data.updateWidget);
    } catch (err) {
      console.error("Error saving display options:", err);
      throw err;
    }
  }, [
    widgetId,
    mode,
    buttonStyle,
    position,
    squareButton,
    minimizeSmallButton,
    showPreview,
    background,
    logo,
    widgetData,
    updateWidget,
  ]);

  // Expose save function to parent component
  useEffect(() => {
    if (onSave && typeof onSave === "function") {
      onSave(handleSaveDisplayOptions);
    }
  }, [onSave, handleSaveDisplayOptions]);

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setMode(newMode);
      handleChildData(newMode);
    }
  };

  const handleButtonStyleChange = (newStyle) => {
    setButtonStyle(newStyle);
  };

  const handleFileUpload = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === "background") {
          setBackground(reader.result);
        } else if (type === "logo") {
          setLogo(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = (type) => {
    if (type === "background") {
      setBackground(null);
    } else if (type === "logo") {
      setLogo(null);
    }
  };

  const handleCopyCode = () => {
    const code = `<button onclick="pathadvice.connect()">Call Agent</button>`;
    navigator.clipboard.writeText(code);
    setCopySuccess(true);
  };

  const buttonStyleOptions = [
    {
      value: "Button",
      label: "Icon Only",
      description: "Compact chat button",
      image: "/static/img/others/button.png",
    },
    {
      value: "Button+Text",
      label: "Icon + Text",
      description: "Button with call-to-action",
      image: "/static/img/others/buttontext.png",
    },
  ];

  return (
    <Grid container spacing={4}>
      {/* Header Section */}
      <Grid item xs={12}>
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Website Integration
        </Typography>
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          Configure how the chat widget appears and behaves on your website
        </Alert>

        {/* Mode Selector */}
        <StyledToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          aria-label="integration mode"
        >
          <ToggleButton value="Standard" aria-label="standard mode">
            <DesktopMac sx={{ mr: 1 }} />
            Standard
          </ToggleButton>
          <ToggleButton value="Developer" aria-label="developer mode">
            <CodeIcon sx={{ mr: 1 }} />
            Developer
          </ToggleButton>
        </StyledToggleButtonGroup>
      </Grid>

      {mode === "Standard" ? (
        <>
          {/* Configuration Panel */}
          <Grid item xs={12} md={6}>
            <ConfigPanel elevation={3}>
              {/* Section 1: Button Style */}
              <Section>
                <SectionTitle variant="h6">
                  <ChatBubble color="primary" />
                  Button Style
                </SectionTitle>
                <Grid container spacing={2}>
                  {buttonStyleOptions.map((option) => (
                    <Grid item xs={6} key={option.value}>
                      <SelectableCard
                        selected={buttonStyle === option.value}
                        onClick={() => handleButtonStyleChange(option.value)}
                      >
                        <Box
                          sx={{
                            height: 100,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f5f7fa",
                            p: 2,
                          }}
                        >
                          <img
                            src={option.image}
                            alt={option.label}
                            style={{
                              maxHeight: "100%",
                              maxWidth: "100%",
                              objectFit: "contain",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.parentElement.innerHTML = `<div style="display: flex; flex-direction: column; align-items: center;"><svg style="font-size: 48px; color: #1976d2;" class="MuiSvgIcon-root"><use href="#chat-icon"></use></svg></div>`;
                            }}
                          />
                        </Box>
                        <CardContent sx={{ py: 1.5, px: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {option.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {option.description}
                              </Typography>
                            </Box>
                            {buttonStyle === option.value && (
                              <Check color="primary" fontSize="small" />
                            )}
                          </Box>
                        </CardContent>
                      </SelectableCard>
                    </Grid>
                  ))}
                </Grid>
              </Section>

              {/* Section 2: Position */}
              <Section>
                <SectionTitle variant="h6">
                  <AlignHorizontalLeft color="primary" />
                  Widget Position
                </SectionTitle>
                <PositionButtonGroup>
                  <PositionButton
                    selected={position === "left"}
                    onClick={() => setPosition("left")}
                  >
                    <AlignHorizontalLeft />
                    Left Side
                  </PositionButton>
                  <PositionButton
                    selected={position === "right"}
                    onClick={() => setPosition("right")}
                  >
                    <AlignHorizontalRight />
                    Right Side
                  </PositionButton>
                </PositionButtonGroup>
              </Section>

              {/* Section 3: Appearance */}
              <Section>
                <SectionTitle variant="h6">
                  <DesktopMac color="primary" />
                  Appearance
                </SectionTitle>
                <AppearanceToggle>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Square Button
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Use squared corners instead of rounded
                    </Typography>
                  </Box>
                  <Switch
                    checked={squareButton}
                    onChange={(e) => setSquareButton(e.target.checked)}
                    color="primary"
                  />
                </AppearanceToggle>
                <AppearanceToggle>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Minimize to Small Button
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Shrink widget when not in use
                    </Typography>
                  </Box>
                  <Switch
                    checked={minimizeSmallButton}
                    onChange={(e) => setMinimizeSmallButton(e.target.checked)}
                    color="primary"
                  />
                </AppearanceToggle>
                <AppearanceToggle>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Show Preview
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Display live preview on this page
                    </Typography>
                  </Box>
                  <Switch
                    checked={showPreview}
                    onChange={(e) => setShowPreview(e.target.checked)}
                    color="primary"
                  />
                </AppearanceToggle>
              </Section>

              {/* Section 4: Branding Uploads */}
              <Section>
                <SectionTitle variant="h6">
                  <ImageIcon color="primary" />
                  Branding
                </SectionTitle>
                <Grid container spacing={2}>
                  {/* Background Upload */}
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Background Image
                    </Typography>
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="background-upload"
                      type="file"
                      onChange={(e) => handleFileUpload(e, "background")}
                    />
                    <label htmlFor="background-upload">
                      <UploadArea hasImage={!!background}>
                        {background ? (
                          <ImagePreviewBox>
                            <img src={background} alt="Background" />
                            <DeleteIconButton
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemoveImage("background");
                              }}
                            >
                              <Delete />
                            </DeleteIconButton>
                          </ImagePreviewBox>
                        ) : (
                          <>
                            <Wallpaper
                              sx={{ fontSize: 32, color: "grey.400", mb: 1 }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Click to upload
                            </Typography>
                          </>
                        )}
                      </UploadArea>
                    </label>
                  </Grid>

                  {/* Logo Upload */}
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Logo Image
                    </Typography>
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id="logo-upload"
                      type="file"
                      onChange={(e) => handleFileUpload(e, "logo")}
                    />
                    <label htmlFor="logo-upload">
                      <UploadArea hasImage={!!logo}>
                        {logo ? (
                          <ImagePreviewBox>
                            <img src={logo} alt="Logo" />
                            <DeleteIconButton
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemoveImage("logo");
                              }}
                            >
                              <Delete />
                            </DeleteIconButton>
                          </ImagePreviewBox>
                        ) : (
                          <>
                            <ImageIcon
                              sx={{ fontSize: 32, color: "grey.400", mb: 1 }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Click to upload
                            </Typography>
                          </>
                        )}
                      </UploadArea>
                    </label>
                  </Grid>
                </Grid>
              </Section>
            </ConfigPanel>
          </Grid>

          {/* Preview Panel */}
          <Grid item xs={12} md={6}>
            <PreviewContainer elevation={3}>
              <SectionTitle variant="h6" sx={{ mb: 2 }}>
                <DesktopMac color="primary" />
                Live Preview
              </SectionTitle>
              {showPreview ? (
                <WebsiteMockup backgroundImage={background}>
                  {/* Website content mockup */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 16,
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: "rgba(255,255,255,0.9)",
                      borderRadius: 1,
                      px: 3,
                      py: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Your Website
                    </Typography>
                  </Box>

                  {/* Logo */}
                  {logo && <LogoPreview src={logo} alt="Logo" />}

                  {/* Widget Button */}
                  <WidgetButtonPreview
                    variant="contained"
                    color="primary"
                    position={position}
                    square={squareButton}
                    hasText={buttonStyle === "Button+Text"}
                  >
                    {buttonStyle === "Button" ? (
                      <Chat />
                    ) : (
                      <>
                        <Chat sx={{ mr: 1 }} />
                        Chat with us
                      </>
                    )}
                  </WidgetButtonPreview>
                </WebsiteMockup>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f5f7fa",
                    borderRadius: 2,
                    minHeight: 400,
                  }}
                >
                  <Typography color="text.secondary">
                    Enable "Show Preview" to see live preview
                  </Typography>
                </Box>
              )}
            </PreviewContainer>
          </Grid>
        </>
      ) : (
        /* Developer Mode */
        <Grid item xs={12}>
          <DeveloperPanel elevation={3}>
            <SectionTitle variant="h6">
              <CodeIcon color="primary" />
              Custom Integration
            </SectionTitle>

            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              Use this code snippet to integrate the chat widget with your own
              custom button design. The widget will open when users click your
              button.
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add this code to your website where you want the chat button to
              appear:
            </Typography>

            <CodeContainer>
              <Code language="html">{`<button onclick="pathadvice.connect()">Call Agent</button>`}</Code>
            </CodeContainer>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                variant="contained"
                startIcon={<ContentCopy />}
                onClick={handleCopyCode}
              >
                Copy Code
              </Button>
              <Typography variant="caption" color="text.secondary">
                Paste this into your website's HTML
              </Typography>
            </Box>

            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                How it works:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  Add the embed script to your website (provided in Step 4)
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  Use <code>pathadvice.connect()</code> to open the chat widget
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  color="text.secondary"
                >
                  Style your button however you want - the widget handles the
                  rest
                </Typography>
              </Box>
            </Box>
          </DeveloperPanel>
        </Grid>
      )}

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        message="Code copied to clipboard!"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Grid>
  );
};

export default DisplayOptions;
